<template>
  <div class="zcg">
    <section class="zcg-card">
      <!-- ============ HEADER ============ -->
      <header class="zcg-header">
        <button type="button" class="zcg-nav-btn" aria-label="Previous week" @click="$emit('prev-week')">
          <i class="fas fa-chevron-left"></i>
        </button>

        <div class="zcg-title-wrap">
          <h2 class="zcg-title">{{ prettyRange }}</h2>
        </div>

        <button type="button" class="zcg-nav-btn" aria-label="Next week" @click="$emit('next-week')">
          <i class="fas fa-chevron-right"></i>
        </button>
      </header>

      <!-- ============ TABLE ============ -->
      <div class="zcg-scroll">
        <table class="zcg-table">
          <colgroup>
            <col class="zcg-col-name" />
            <col v-for="day in safeWeekDays" :key="'cg-' + day.dow" />
          </colgroup>

          <thead>
            <tr>
              <th class="zcg-th-name" scope="col">ZONE</th>
              <th
                v-for="day in safeWeekDays"
                :key="day.dateStr"
                scope="col"
                class="zcg-th-day"
                :class="{ 'is-today': day.isToday, 'is-weekend': day.isWeekend }"
              >
                <button type="button" class="zcg-dayhead" :aria-label="'Open ' + day.dateLabel"
                        @click="$emit('open-day', day)">
                  <span class="zcg-dow">{{ day.dayShort }}</span>
                  <span class="zcg-date">{{ day.dateOnly }}</span>
                </button>
              </th>
            </tr>
          </thead>

          <tbody>
            <!-- ============ ALLOWED ROW ============ -->
            <tr class="zcg-summary-row zcg-row-allowed">
              <th class="zcg-td-name zcg-summary-name" scope="row">ALLOWED</th>
              <td
                v-for="day in safeWeekDays"
                :key="'allowed-' + day.dow"
                class="zcg-td zcg-summary-td"
                :class="{ 'is-today': day.isToday, 'is-weekend': day.isWeekend }"
              >
                <span class="zcg-summary-val zcg-val-allowed">
                  {{ formatTruckTriple(day.dow) }}
                </span>
              </td>
            </tr>

            <!-- ============ SCHEDULED ROW ============ -->
            <tr class="zcg-summary-row zcg-row-scheduled">
              <th class="zcg-td-name zcg-summary-name" scope="row">SCHEDULED</th>
              <td
                v-for="day in safeWeekDays"
                :key="'sched-' + day.dow"
                class="zcg-td zcg-summary-td"
                :class="{ 'is-today': day.isToday, 'is-weekend': day.isWeekend }"
              >
                <span
                  class="zcg-summary-val zcg-val-scheduled"
                  :class="{ 'is-over': dayTotalOverflow(day) > 0 }"
                >
                  {{ formatTriple(dayScheduledTotals(day.dateStr)) }}
                </span>
              </td>
            </tr>

            <!-- ============ ZONE ROWS ============ -->
            <tr v-for="zone in safeZones" :key="zone.id" class="zcg-row">
              <th class="zcg-td-name zcg-zone-name-cell" scope="row">
                <span class="zcg-zone-bar" :style="{ background: zone.color || '#1e3a5f' }"></span>
                <span class="zcg-zone-text">
                  <span class="zcg-zone-code">{{ zone.name }}</span>
                </span>
              </th>

              <td
                v-for="day in safeWeekDays"
                :key="zone.id + '-' + day.dow"
                class="zcg-td"
                :class="cellClass(zone, day)"
              >
                <div class="zcg-slot" :class="levelClass(zone.id, day)">
                  <button
                    type="button"
                    class="zcg-pill"
                    :class="cellLabelClass(zone.id, day)"
                    :aria-label="'Open ' + day.dateLabel + ', ' + (zone.code || '')"
                    @click="$emit('open-day', day)"
                  >
                    <i v-if="overflow(zone.id, day) > 0" class="fas fa-exclamation-triangle"></i>
                    {{ cellLabel(zone.id, day) }}
                  </button>

                  <div class="zcg-bar">
                    <div class="zcg-bar-fill" :style="{ width: levelHeight(zone.id, day) + '%' }"></div>
                  </div>

                  <div class="zcg-slot-ctrl">
                    <label class="zcg-switch"
                           :title="cellValue(zone.id, day.dow).is_active ? 'Stop accepting orders this day' : 'Accept orders this day'">
                      <input
                        type="checkbox"
                        :checked="cellValue(zone.id, day.dow).is_active"
                        @change="toggle(zone.id, day.dow, $event.target.checked)"
                      />
                      <span class="zcg-track"></span>
                      <span class="zcg-sr">Accept orders</span>
                    </label>

                    <label class="zcg-max" title="Maximum orders for this zone on this day">
                      <span>Max</span>
                      <input
                        type="number"
                        min="0"
                        :max="maxAllowedFor(zone.id, day.dow)"
                        :value="cellValue(zone.id, day.dow).max_orders"
                        @change="maxChange(zone.id, day.dow, $event.target.value)"
                        @keydown.enter="$event.target.blur()"
                      />
                    </label>
                  </div>

                  <i v-if="savingCells[zone.id + '-' + day.dow]" class="fas fa-circle-notch fa-spin zcg-saving"></i>
                </div>
              </td>
            </tr>

            <tr v-if="!safeZones.length">
              <td :colspan="(safeWeekDays.length || 7) + 1" class="zcg-empty">
                <i class="fas fa-map-marked-alt"></i>
                <div class="zcg-empty-title">No delivery zones yet</div>
                <div class="zcg-empty-sub">
                  Add zones in <b>Delivery Zone Management</b>, then set their daily capacity here.
                </div>
              </td>
            </tr>
          </tbody>

          <tfoot v-if="safeZones.length">
            <tr>
              <th class="zcg-td-name zcg-tf-label" scope="row">Daily total</th>
              <td
                v-for="day in safeWeekDays"
                :key="'foot-' + day.dow"
                class="zcg-tf"
                :class="{ 'is-today': day.isToday, 'is-weekend': day.isWeekend, 'tf-over': dayTotalOverflow(day) > 0 }"
              >
                <div class="zcg-tf-nums">
                  <b>{{ dayTotalScheduled(day.dateStr) }}</b>
                  <span>/ {{ dayTruckCapacity(day.dow) }}</span>
                </div>
                <div class="zcg-tf-bar">
                  <div class="zcg-tf-fill" :class="dayBarClass(day)" :style="{ width: dayBarWidth(day) + '%' }"></div>
                </div>
                <div v-if="dayTotalOverflow(day) > 0" class="zcg-tf-over">+{{ dayTotalOverflow(day) }} over</div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'ZoneCapacityGrid',
  props: {
    zones:        { type: Array,  default: () => [] },
    matrix:       { type: Object, default: () => ({}) },
    countsByDate: { type: Object, default: () => ({}) },
    weekDays:     { type: Array,  default: () => [] },
    savingCells:  { type: Object, default: () => ({}) },
    weekStart:    { type: [Object, String], default: null },
    weekEnd:      { type: [Object, String], default: null },
    weekNumber:   { type: [Number, String], default: null },
    ordersTotal:   { type: Number, default: null },
    assignedTotal: { type: Number, default: null },

    truckAvailability:     { type: Object, default: () => ({}) },
    dailyCapacityByDow:    { type: Object, default: () => ({}) },
    assignedCapacityByDow: { type: Object, default: () => ({}) },
  },
  computed: {
    safeZones() { return Array.isArray(this.zones) ? this.zones : [] },

    safeWeekDays() {
      if (!Array.isArray(this.weekDays)) return []
      return this.weekDays.map(d => ({
        date:       d && d.date,
        dateStr:    (d && d.dateStr)    || '',
        dateLabel:  (d && d.dateLabel)  || '',
        dateOnly:   (d && d.dateOnly)   || '',
        monthShort: (d && d.monthShort) || '',
        dayShort:   (d && d.dayShort)   || '',
        dow:        (d && d.dow)        || 1,
        isToday:    !!(d && d.isToday),
        isWeekend:  !!(d && d.isWeekend),
        isPast:     !!(d && d.isPast),
      }))
    },

    weekStartLabel() {
      if (!this.weekStart) {
        const d = this.safeWeekDays[0]
        return d && d.dateLabel ? d.dateLabel : ''
      }
      return this._fmtDate(this.weekStart)
    },
    weekEndLabel() {
      if (!this.weekEnd) {
        const d = this.safeWeekDays[6]
        return d && d.dateLabel ? d.dateLabel : ''
      }
      return this._fmtDate(this.weekEnd)
    },

    prettyRange() {
      const s = this.weekStart
      const e = this.weekEnd
      if (s && e) {
        const sDate = this._asDate(s)
        const eDate = this._asDate(e)
        if (sDate && eDate) {
          const sd = this._ordinal(sDate.getDate())
          const ed = this._ordinal(eDate.getDate())
          const sMon = this._monthName(sDate.getMonth())
          const eMon = this._monthName(eDate.getMonth())
          if (sDate.getMonth() === eDate.getMonth() && sDate.getFullYear() === eDate.getFullYear()) {
            return `${sd} – ${ed} ${eMon} ${eDate.getFullYear()}`
          }
          if (sDate.getFullYear() === eDate.getFullYear()) {
            return `${sd} ${sMon} – ${ed} ${eMon} ${eDate.getFullYear()}`
          }
          return `${sd} ${sMon} ${sDate.getFullYear()} – ${ed} ${eMon} ${eDate.getFullYear()}`
        }
      }
      return `${this.weekStartLabel} – ${this.weekEndLabel}`
    },

    resolvedWeekNumber() {
      if (this.weekNumber) return this.weekNumber
      if (this.weekStart && typeof this.weekStart.isoWeek === 'function') return this.weekStart.isoWeek()
      return null
    },
  },
  methods: {
    _fmtDate(d) {
      if (!d) return ''
      if (typeof d === 'string') return d
      if (typeof d.format === 'function') return d.format('MMM D')
      return String(d)
    },
    _asDate(d) {
      if (!d) return null
      if (d instanceof Date) return d
      if (typeof d.toDate === 'function') return d.toDate()
      if (typeof d === 'string') { const x = new Date(d); return isNaN(x) ? null : x }
      return null
    },
    _ordinal(n) {
      const s = ['th','st','nd','rd']
      const v = n % 100
      return n + (s[(v - 20) % 10] || s[v] || s[0])
    },
    _monthName(i) {
      return ['January','February','March','April','May','June',
              'July','August','September','October','November','December'][i] || ''
    },

    /* ============================================================
       Truck helpers
       ============================================================ */

  dayTruck(dow) {
  const day = this.safeWeekDays.find(d => d.dow === dow)
  if (!day) {
    return { trucks_available: 0, max_orders_per_truck: 0,
             total_capacity: 0, available_cubes: 0 }
  }

  const rec = this.truckAvailability[day.dateStr]
  if (!rec) {
    return { trucks_available: 0, max_orders_per_truck: 0,
             total_capacity: 0, available_cubes: 0 }
  }

  // Prefer real cubes from backend; fall back to a parsed value,
  // then to total_capacity * 250 as a last resort.
  let cubes = rec.available_cubes
  if (cubes == null && rec.notes) {
    const m = /available_cubes=(\d+)/.exec(rec.notes)
    if (m) cubes = parseInt(m[1], 10)
  }
  if (cubes == null) {
    cubes = (rec.total_capacity || 0) * 250
  }

  return {
    trucks_available:     rec.trucks_available     || 0,
    max_orders_per_truck: rec.max_orders_per_truck || 0,
    total_capacity:       rec.total_capacity       || 0,
    available_cubes:      cubes,
  }
},
    // ALLOWED cell → `trucks - total_capacity - (total_capacity × 100)`
 formatTruckTriple(dow) {
  const t = this.dayTruck(dow)
  const trucks   = t.trucks_available || 0
  const totalCap = t.total_capacity   || 0
  const cubes    = t.available_cubes  || 0
  return `${trucks}-${totalCap}-${cubes}`
},

    dayTruckCapacity(dow) {
      return this.dayTruck(dow).total_capacity || 0
    },

    dayAssignedTotal(dow) {
      if (this.assignedCapacityByDow && this.assignedCapacityByDow[dow] != null) {
        return this.assignedCapacityByDow[dow]
      }
      let total = 0
      Object.keys(this.matrix || {}).forEach(zid => {
        const cell = (this.matrix[zid] || {})[dow]
        if (cell && cell.is_active) total += (cell.max_orders || 0)
      })
      return total
    },

    maxAllowedFor(zone_id, dow) {
      const dayTotal = this.dayTruckCapacity(dow)
      if (!dayTotal) return 0
      let otherTotal = 0
      Object.keys(this.matrix || {}).forEach(zid => {
        if (String(zid) === String(zone_id)) return
        const cell = (this.matrix[zid] || {})[dow]
        if (cell && cell.is_active) otherTotal += (cell.max_orders || 0)
      })
      return Math.max(0, dayTotal - otherTotal)
    },

    /* ---------- Cell shape ---------- */
    cellValue(zone_id, dow) {
      const z = this.matrix && this.matrix[zone_id]
      if (!z || !z[dow]) {
        return { is_active: true, max_orders: 0, priority: 0, allowed: null }
      }
      return z[dow]
    },
    allowedOf(zone_id, dow) {
      const cell = this.cellValue(zone_id, dow)
      const a = cell.allowed || {}
      return {
        trucks:     a.trucks     != null ? a.trucks     : 0,
        deliveries: a.deliveries != null ? a.deliveries : (cell.max_orders || 0),
        cubes:      a.cubes      != null ? a.cubes      : 0,
      }
    },

    countsFor(zone_id, dateStr) {
      const byDate = (this.countsByDate && this.countsByDate[dateStr]) || {}
      const z = byDate[zone_id]
      return {
        scheduled:       (z && z.scheduled)       || 0,
        assigned:        (z && z.assigned)        || 0,
        scheduledCubes:  (z && z.scheduledCubes)  || 0,
        scheduledTrucks: (z && z.scheduledTrucks) || 0,
      }
    },
    scheduledCount(zone_id, dateStr) { return this.countsFor(zone_id, dateStr).scheduled },
    assignedCount(zone_id, dateStr)  { return this.countsFor(zone_id, dateStr).assigned },

    /* ---------- Formatting ---------- */
    formatTriple({ trucks = 0, deliveries = 0, cubes = 0 } = {}) {
      return `${trucks}-${deliveries}-${cubes}`
    },

    cellLabel(zone_id, day) {
      const cell = this.cellValue(zone_id, day.dow)
      if (!cell.is_active) return 'OFF'
      const scheduled = this.scheduledCount(zone_id, day.dateStr)
      const allowed = this.allowedOf(zone_id, day.dow)
      if (allowed.deliveries > 0) return `ON-${allowed.deliveries}-${scheduled}`
      return `ON-MAX-${scheduled}`
    },
    cellLabelClass(zone_id, day) {
      const cell = this.cellValue(zone_id, day.dow)
      if (!cell.is_active) return 'pill-off'
      if (this.overflow(zone_id, day) > 0) return 'pill-over'
      const allowed = this.allowedOf(zone_id, day.dow)
      const scheduled = this.scheduledCount(zone_id, day.dateStr)
      if (allowed.deliveries > 0 && scheduled >= allowed.deliveries) return 'pill-full'
      return 'pill-on'
    },

    /* ---------- Row/col totals ---------- */
    dayScheduledTotals(dateStr) {
      const byZone = (this.countsByDate && this.countsByDate[dateStr]) || {}
      let trucks = 0, deliveries = 0, cubes = 0
      Object.keys(byZone).forEach(zid => {
        const z = byZone[zid] || {}
        trucks     += (z.scheduledTrucks || 0)
        deliveries += (z.scheduled || 0)
        cubes      += (z.scheduledCubes || 0)
      })
      return { trucks, deliveries, cubes }
    },

    /* ---------- Cell state ---------- */
    overflow(zone_id, day) {
      const cell = this.cellValue(zone_id, day.dow)
      if (!cell.is_active) return 0
      const allowed = this.allowedOf(zone_id, day.dow)
      if (allowed.deliveries === 0) return 0
      return Math.max(0, this.scheduledCount(zone_id, day.dateStr) - allowed.deliveries)
    },
    isFull(zone_id, day) {
      const cell = this.cellValue(zone_id, day.dow)
      if (!cell.is_active) return false
      const allowed = this.allowedOf(zone_id, day.dow)
      if (allowed.deliveries === 0) return false
      return this.scheduledCount(zone_id, day.dateStr) >= allowed.deliveries
    },
    cellClass(zone, day) {
      return {
        'is-off':     !this.cellValue(zone.id, day.dow).is_active,
        'is-today':   day.isToday,
        'is-weekend': day.isWeekend,
        'is-past':    day.isPast,
      }
    },
    levelClass(zone_id, day) {
      const cell = this.cellValue(zone_id, day.dow)
      if (!cell.is_active) return 'lvl-off'
      if (this.overflow(zone_id, day) > 0) return 'lvl-over'
      if (this.isFull(zone_id, day)) return 'lvl-full'
      if (this.scheduledCount(zone_id, day.dateStr) > 0) return 'lvl-ok'
      return 'lvl-empty'
    },
    levelHeight(zone_id, day) {
      const cell = this.cellValue(zone_id, day.dow)
      if (!cell.is_active) return 0
      const allowed = this.allowedOf(zone_id, day.dow)
      if (allowed.deliveries === 0) return 0
      return Math.min(100, Math.round((this.scheduledCount(zone_id, day.dateStr) / allowed.deliveries) * 100))
    },

    /* ---------- Footer / aggregates ---------- */
    dayTotalScheduled(dateStr) {
      const byZone = (this.countsByDate && this.countsByDate[dateStr]) || {}
      let total = 0
      Object.keys(byZone).forEach(zid => { total += ((byZone[zid] || {}).scheduled || 0) })
      return total
    },
    dayTotalOverflow(day) {
      let total = 0
      this.safeZones.forEach(z => { total += this.overflow(z.id, day) })
      return total
    },
    dayBarWidth(day) {
      const cap = this.dayTruckCapacity(day.dow)
      const scheduled = this.dayTotalScheduled(day.dateStr)
      if (cap === 0) return scheduled > 0 ? 100 : 0
      return Math.min(100, Math.round((scheduled / cap) * 100))
    },
    dayBarClass(day) {
      const cap = this.dayTruckCapacity(day.dow)
      const scheduled = this.dayTotalScheduled(day.dateStr)
      if (cap === 0) return 'bar-off'
      if (scheduled > cap) return 'bar-over'
      if (scheduled === cap) return 'bar-full'
      return 'bar-ok'
    },
    zoneWeekTotal(zone_id) {
      let n = 0
      this.safeWeekDays.forEach(d => { n += this.scheduledCount(zone_id, d.dateStr) })
      return n
    },

    toggle(zone_id, dow, checked) {
      this.$emit('toggle-cell', { zone_id, dow, is_active: checked })
    },
    maxChange(zone_id, dow, value) {
      this.$emit('max-change', { zone_id, dow, max_orders: value })
    },
  },
}
</script>

<style scoped>
/* ============================================================
   TOKENS
   ============================================================ */
.zcg {
  --ink:           #1c2430;
  --muted:         #5f6b7a;
  --faint:         #8a95a3;
  --line:          #e4e8ee;
  --line-strong:   #d3d9e1;
  --surface:       #ffffff;
  --canvas:        #f6f8fa;
  --accent:        #1f5fa8;
  --today-col:     #f6f9fd;
  --ok:            #1f7a4d;
  --ok-soft:       rgba(31, 122, 77, 0.12);
  --full:          #c8870f;
  --full-soft:     rgba(200, 135, 15, 0.16);
  --over:          #c4423a;
  --over-soft:     rgba(196, 66, 58, 0.14);
  --off:           #a3adb9;

  --pill-on-bg:    #1e3a5f;
  --pill-on-fg:    #ffffff;
  --pill-off-bg:   #fbe7e6;
  --pill-off-fg:   #b3382f;
  --pill-full-bg:  #fbeecb;
  --pill-full-fg:  #8a5a06;
  --pill-over-bg:  #f6cfcb;
  --pill-over-fg:  #8a201a;

  width: 100%;
  color: var(--ink);
  font-family: Inter, 'Segoe UI', system-ui, -apple-system, Roboto, sans-serif;
  font-size: 14px;
  line-height: 1.4;
  font-variant-numeric: tabular-nums;
}

.zcg-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
}

/* ============================================================
   HEADER
   ============================================================ */
.zcg-header {
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  gap: 12px;
  padding: 14px 18px 8px;
}
.zcg-nav-btn {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--accent);
  font-size: 15px;
  cursor: pointer;
}
.zcg-nav-btn:hover { background: #eef3fb; }
.zcg-nav-btn:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }

.zcg-title-wrap { text-align: center; min-width: 0; }
.zcg-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--accent);
  letter-spacing: -0.01em;
}

/* ============================================================
   TABLE
   ============================================================ */
.zcg-scroll { width: 100%; overflow-x: auto; }

.zcg-table {
  width: 100%;
  min-width: 1080px;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}
.zcg-col-name { width: 200px; }

.zcg-table th,
.zcg-table td {
  border-right: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  text-align: center;
  vertical-align: middle;
}
.zcg-table tr > :last-child { border-right: 0; }
.zcg-table thead tr > th { border-top: 1px solid var(--line); }
.zcg-table thead tr > th:first-child { border-left: 1px solid var(--line); }
.zcg-table thead tr > th:last-child  { border-right: 1px solid var(--line); }

.zcg-th-name {
  padding: 14px 16px;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.6px;
  color: #33465b;
  text-transform: uppercase;
  text-align: left;
  background: var(--surface);
  border-right-color: var(--line-strong) !important;
}

.zcg-th-day { padding: 0; background: var(--surface); font-weight: normal; }
.zcg-th-day.is-weekend { background: #fafbfc; }
.zcg-th-day.is-today   { background: var(--today-col); }

.zcg-dayhead {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 12px 8px;
  border: 0;
  background: transparent;
  font: inherit;
  color: inherit;
  cursor: pointer;
}
.zcg-dow {
  font-size: 25px; font-weight: 800; color: #33465b;
  text-transform: uppercase; letter-spacing: 0.6px; white-space: nowrap;
}
.zcg-date { font-size: 20px; font-weight: 500; color: var(--muted); white-space: nowrap; }
.is-today .zcg-dow,
.is-today .zcg-date { color: var(--accent); }

.zcg-td-name {
  padding: 14px 16px;
  text-align: left;
  font-weight: 700;
  color: var(--ink);
  font-size: 14px;
  background: var(--surface);
  border-right-color: var(--line-strong) !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ============================================================
   SUMMARY ROWS (ALLOWED / SCHEDULED)
   ============================================================ */
.zcg-summary-row .zcg-summary-name {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.6px;
  color: #33465b;
  text-transform: uppercase;
  background: var(--surface);
}
.zcg-summary-td {
  padding: 12px 10px;
  background: var(--surface);
}
.zcg-summary-td.is-weekend { background: #fafbfc; }
.zcg-summary-td.is-today   { background: var(--today-col); }

.zcg-summary-val {
  display: inline-block;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 0.2px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.zcg-val-allowed   { color: var(--ok); }
.zcg-val-scheduled { color: var(--over); }
.zcg-val-scheduled.is-over { color: #8a201a; text-decoration: underline; }

.zcg-row-allowed   > * { border-top: 2px solid rgba(31, 122, 77, 0.35); }
.zcg-row-scheduled > * { border-top: 1px solid var(--line-strong); }

/* ============================================================
   ZONE ROWS
   ============================================================ */
.zcg-row .zcg-td {
  padding: 8px;
  vertical-align: top;
  background: var(--surface);
}
.zcg-row .zcg-td.is-weekend { background: #fafbfc; }
.zcg-row .zcg-td.is-today   { background: var(--today-col); }

.zcg-zone-name-cell {
  position: relative;
  vertical-align: middle !important;
}
.zcg-zone-bar {
  position: absolute;
  left: 0; top: 8px; bottom: 8px;
  width: 4px; border-radius: 2px;
}
.zcg-zone-text {
  display: flex; flex-direction: column; gap: 2px;
  padding-left: 10px; min-width: 0;
}
.zcg-zone-code {
  font-size: 14px; font-weight: 700; color: var(--ink);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.zcg-zone-sub {
  font-size: 12px; color: var(--muted); font-weight: 500;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* ============================================================
   CELL STACK
   ============================================================ */
.zcg-slot {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
  transition: border-color 0.12s, background 0.12s;
}
.zcg-slot:hover { border-color: var(--line-strong); }

.lvl-off {
  background: repeating-linear-gradient(135deg, #f1f3f6 0 6px, #e9edf1 6px 12px);
}
.lvl-off .zcg-bar { display: none; }
.lvl-off .zcg-pill { background: var(--pill-off-bg); color: var(--pill-off-fg); }

.zcg-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%;
  min-height: 30px;
  padding: 0 12px;
  border: 0;
  border-radius: 999px;
  font: inherit;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: filter 0.12s ease, transform 0.08s ease;
  flex-shrink: 0;
}
.zcg-pill:hover  { filter: brightness(0.95); }
.zcg-pill:active { transform: translateY(1px); }
.zcg-pill:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
.zcg-pill i { font-size: 10px; }

.pill-on   { background: var(--pill-on-bg);   color: var(--pill-on-fg); }
.pill-off  { background: var(--pill-off-bg);  color: var(--pill-off-fg); }
.pill-full { background: var(--pill-full-bg); color: var(--pill-full-fg); }
.pill-over { background: var(--pill-over-bg); color: var(--pill-over-fg); }

.zcg-bar {
  position: relative;
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: #eef1f4;
  overflow: hidden;
  flex-shrink: 0;
}
.zcg-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.35s ease;
}
.lvl-ok    .zcg-bar-fill { background: var(--ok); }
.lvl-full  .zcg-bar-fill { background: var(--full); }
.lvl-over  .zcg-bar-fill { background: var(--over); }
.lvl-empty .zcg-bar-fill { background: transparent; }

.zcg-slot-ctrl {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  background: #f6f8fa;
  flex-shrink: 0;
}

.zcg-switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 32px;
  height: 18px;
  flex-shrink: 0;
  margin: 0;
  cursor: pointer;
}
.zcg-switch input {
  position: absolute; inset: 0;
  opacity: 0; margin: 0; cursor: pointer;
}
.zcg-track {
  position: absolute; inset: 0;
  border-radius: 999px;
  background: #c4ccd6;
  transition: background 0.15s;
  pointer-events: none;
}
.zcg-track::before {
  content: '';
  position: absolute;
  top: 2px; left: 2px;
  width: 14px; height: 14px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.25);
  transition: transform 0.15s;
}
.zcg-switch input:checked + .zcg-track { background: var(--ok); }
.zcg-switch input:checked + .zcg-track::before { transform: translateX(14px); }
.zcg-switch input:focus-visible + .zcg-track { outline: 2px solid var(--accent); outline-offset: 2px; }

.zcg-max {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin: 0;
  font-size: 11px;
  color: var(--muted);
  font-weight: 600;
}
.zcg-max input {
  width: 48px;
  height: 24px;
  padding: 0 4px;
  border: 1px solid var(--line-strong);
  border-radius: 5px;
  background: #fff;
  color: var(--ink);
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
}
.zcg-max input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(31, 95, 168, 0.15);
}

.zcg-saving {
  position: absolute;
  top: 6px; right: 6px;
  z-index: 2;
  font-size: 12px;
  color: var(--accent);
}

.zcg-td.is-past .zcg-slot { opacity: 0.85; }

/* ============================================================
   FOOTER
   ============================================================ */
.zcg-tf-label {
  padding: 12px 16px;
  font-size: 12px; font-weight: 800;
  color: #33465b; text-transform: uppercase; letter-spacing: 0.6px;
  vertical-align: middle;
  background: var(--canvas);
  white-space: nowrap;
}
.zcg-tf {
  padding: 10px 12px;
  background: var(--canvas);
  border-bottom: 0 !important;
}
.zcg-tf.tf-over { background: #fbefee; }
.zcg-tf-nums {
  display: flex; align-items: baseline; gap: 5px;
  margin-bottom: 6px; flex-wrap: wrap; justify-content: center;
}
.zcg-tf-nums b { font-size: 16px; font-weight: 700; }
.zcg-tf-nums span { font-size: 12px; color: var(--muted); }
.zcg-tf-bar { height: 4px; border-radius: 2px; background: var(--line); overflow: hidden; }
.zcg-tf-fill { height: 100%; transition: width 0.35s ease; }
.bar-ok   { background: var(--ok); }
.bar-full { background: var(--full); }
.bar-over { background: var(--over); }
.bar-off  { background: var(--off); }
.zcg-tf-over { margin-top: 6px; font-size: 12px; font-weight: 700; color: var(--over); white-space: nowrap; }

/* ============================================================
   EMPTY STATE
   ============================================================ */
.zcg-empty { padding: 56px 20px !important; text-align: center; color: var(--muted); }
.zcg-empty i { display: block; font-size: 32px; color: var(--line-strong); margin-bottom: 12px; }
.zcg-empty-title { font-size: 16px; font-weight: 700; color: var(--ink); margin-bottom: 4px; }
.zcg-empty-sub { font-size: 13px; }

/* ============================================================
   A11Y & MOTION
   ============================================================ */
.zcg-sr {
  position: absolute; width: 1px; height: 1px;
  overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap;
}
.zcg-btn-sm:focus-visible,
.zcg-nav-btn:focus-visible,
.zcg-icon-btn:focus-visible,
.zcg-dayhead:focus-visible,
.zcg-pill:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: -2px;
  border-radius: 6px;
}

@media (prefers-reduced-motion: reduce) {
  .zcg-tf-fill, .zcg-track, .zcg-track::before, .zcg-pill, .zcg-bar-fill { transition: none; }
}

@media (max-width: 1100px) { .zcg-legend { display: none; } }
@media (max-width: 768px) {
  .zcg { font-size: 13px; }
  .zcg-title { font-size: 17px; }
  .zcg-col-name { width: 140px; }
  .zcg-th-name, .zcg-td-name { padding: 12px 10px; }
  .zcg-summary-val { font-size: 13px; }
  .zcg-pill { font-size: 10px; padding: 0 8px; min-height: 24px; }
  .zcg-max input { width: 40px; }
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/ZoneCapacityGrid.vue